import React, { useState } from 'react';
import { Send, MessageCircle, Mail, Phone, CheckCircle, AlertCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const WHATSAPP_NUMBER = '+393453509743';
const WHATSAPP_MESSAGE = encodeURIComponent('Ciao! Vorrei ricevere informazioni sulla consulenza energetica e finanza agevolata.');

const Contact = () => {
  const [contactMethod, setContactMethod] = useState('email');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    telefono: '',
    azienda: '',
    tipo_richiesta: 'entrambi',
    messaggio: '',
    privacy_accepted: false,
    marketing_accepted: false
  });

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.privacy_accepted) {
      setSubmitStatus({ type: 'error', message: 'È necessario accettare l\'informativa sulla privacy' });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      if (response.data.success) {
        setSubmitStatus({ type: 'success', message: response.data.message });
        setFormData({
          nome: '',
          cognome: '',
          email: '',
          telefono: '',
          azienda: '',
          tipo_richiesta: 'entrambi',
          messaggio: '',
          privacy_accepted: false,
          marketing_accepted: false
        });
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || 'Si è verificato un errore. Riprova più tardi.';
      setSubmitStatus({ type: 'error', message: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const openWhatsApp = () => {
    window.open(`https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}?text=${WHATSAPP_MESSAGE}`, '_blank');
  };

  return (
    <section id="contatti" data-testid="contact-section" className="pt-20 pb-32 md:py-32 relative bg-[#0a0f1a]">
      {/* Background */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-[#5CBDB0]/5 rounded-full blur-3xl" />

      <div className="container-custom relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="text-sm uppercase tracking-widest font-bold text-[#5CBDB0] mb-4 block">
            Contattaci
          </span>
          <h2 className="font-heading text-3xl md:text-5xl font-semibold text-white mb-6">
            Inizia a <span className="gradient-text">Risparmiare</span> Oggi
          </h2>
          <p className="text-white/60 text-base md:text-lg max-w-2xl mx-auto">
            Compila il form per una consulenza gratuita o contattaci direttamente su WhatsApp.
            Ti risponderemo entro 24 ore.
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Contact Method Toggle */}
          <div className="flex justify-center gap-4 mb-10">
            <button
              onClick={() => setContactMethod('email')}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                contactMethod === 'email'
                  ? 'bg-[#5CBDB0] text-black'
                  : 'bg-white/5 text-white/70 hover:bg-white/10'
              }`}
              data-testid="contact-method-email"
            >
              <Mail className="w-5 h-5" />
              Via Email
            </button>
            <button
              onClick={() => { setContactMethod('whatsapp'); openWhatsApp(); }}
              className={`flex items-center gap-2 px-6 py-3 rounded-full font-semibold transition-all ${
                contactMethod === 'whatsapp'
                  ? 'bg-[#25D366] text-white'
                  : 'bg-white/5 text-white/70 hover:bg-white/10'
              }`}
              data-testid="contact-method-whatsapp"
            >
              <MessageCircle className="w-5 h-5" />
              Via WhatsApp
            </button>
          </div>

          {/* Contact Form */}
          <div className="form-container rounded-3xl p-8 md:p-12">
            <form onSubmit={handleSubmit} data-testid="contact-form">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Nome */}
                <div>
                  <label htmlFor="nome" className="block text-white/70 text-sm mb-2">
                    Nome *
                  </label>
                  <input
                    type="text"
                    id="nome"
                    name="nome"
                    value={formData.nome}
                    onChange={handleInputChange}
                    required
                    minLength={2}
                    className="input-field w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/20 outline-none"
                    placeholder="Il tuo nome"
                    data-testid="input-nome"
                  />
                </div>

                {/* Cognome */}
                <div>
                  <label htmlFor="cognome" className="block text-white/70 text-sm mb-2">
                    Cognome *
                  </label>
                  <input
                    type="text"
                    id="cognome"
                    name="cognome"
                    value={formData.cognome}
                    onChange={handleInputChange}
                    required
                    minLength={2}
                    className="input-field w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/20 outline-none"
                    placeholder="Il tuo cognome"
                    data-testid="input-cognome"
                  />
                </div>

                {/* Email */}
                <div>
                  <label htmlFor="email" className="block text-white/70 text-sm mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    required
                    className="input-field w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/20 outline-none"
                    placeholder="email@azienda.it"
                    data-testid="input-email"
                  />
                </div>

                {/* Telefono */}
                <div>
                  <label htmlFor="telefono" className="block text-white/70 text-sm mb-2">
                    Telefono *
                  </label>
                  <input
                    type="tel"
                    id="telefono"
                    name="telefono"
                    value={formData.telefono}
                    onChange={handleInputChange}
                    required
                    minLength={8}
                    className="input-field w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/20 outline-none"
                    placeholder="+39 333 1234567"
                    data-testid="input-telefono"
                  />
                </div>

                {/* Azienda */}
                <div className="md:col-span-2">
                  <label htmlFor="azienda" className="block text-white/70 text-sm mb-2">
                    Nome Azienda
                  </label>
                  <input
                    type="text"
                    id="azienda"
                    name="azienda"
                    value={formData.azienda}
                    onChange={handleInputChange}
                    className="input-field w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/20 outline-none"
                    placeholder="La tua azienda S.r.l."
                    data-testid="input-azienda"
                  />
                </div>

                {/* Tipo Richiesta */}
                <div className="md:col-span-2">
                  <label className="block text-white/70 text-sm mb-3">
                    Di cosa hai bisogno? *
                  </label>
                  <div className="flex flex-wrap gap-4">
                    {[
                      { value: 'consulenza_energetica', label: 'Consulenza Energetica' },
                      { value: 'finanza_agevolata', label: 'Finanza Agevolata' },
                      { value: 'entrambi', label: 'Entrambi' }
                    ].map((option) => (
                      <label 
                        key={option.value} 
                        className="flex items-center gap-2 cursor-pointer"
                        data-testid={`radio-${option.value}`}
                      >
                        <input
                          type="radio"
                          name="tipo_richiesta"
                          value={option.value}
                          checked={formData.tipo_richiesta === option.value}
                          onChange={handleInputChange}
                          className="w-4 h-4 accent-[#5CBDB0]"
                        />
                        <span className="text-white/70 text-sm">{option.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Messaggio */}
                <div className="md:col-span-2">
                  <label htmlFor="messaggio" className="block text-white/70 text-sm mb-2">
                    Messaggio
                  </label>
                  <textarea
                    id="messaggio"
                    name="messaggio"
                    value={formData.messaggio}
                    onChange={handleInputChange}
                    rows={4}
                    className="input-field w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-white/20 outline-none resize-none"
                    placeholder="Descrivi brevemente le tue esigenze..."
                    data-testid="input-messaggio"
                  />
                </div>

                {/* Privacy Consent */}
                <div className="md:col-span-2 space-y-4">
                  <label className="flex items-start gap-3 cursor-pointer" data-testid="checkbox-privacy">
                    <input
                      type="checkbox"
                      name="privacy_accepted"
                      checked={formData.privacy_accepted}
                      onChange={handleInputChange}
                      className="w-5 h-5 mt-0.5 accent-[#5CBDB0] rounded"
                      required
                    />
                    <span className="text-white/60 text-sm">
                      Ho letto e accetto l'<Link to="/privacy-policy" className="text-[#5CBDB0] hover:underline">Informativa sulla Privacy</Link> ai sensi del Regolamento UE 2016/679 (GDPR) *
                    </span>
                  </label>

                  <label className="flex items-start gap-3 cursor-pointer" data-testid="checkbox-marketing">
                    <input
                      type="checkbox"
                      name="marketing_accepted"
                      checked={formData.marketing_accepted}
                      onChange={handleInputChange}
                      className="w-5 h-5 mt-0.5 accent-[#5CBDB0] rounded"
                    />
                    <span className="text-white/60 text-sm">
                      Acconsento al trattamento dei miei dati per finalità di marketing e comunicazioni promozionali
                    </span>
                  </label>
                </div>
              </div>

              {/* Submit Status */}
              {submitStatus && (
                <div 
                  className={`mt-6 p-4 rounded-lg flex items-center gap-3 ${
                    submitStatus.type === 'success' 
                      ? 'bg-green-500/10 border border-green-500/30' 
                      : 'bg-red-500/10 border border-red-500/30'
                  }`}
                  data-testid="submit-status"
                >
                  {submitStatus.type === 'success' ? (
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                  )}
                  <span className={submitStatus.type === 'success' ? 'text-green-500' : 'text-red-500'}>
                    {submitStatus.message}
                  </span>
                </div>
              )}

              {/* Submit Button */}
              <div className="mt-8 flex flex-col sm:flex-row gap-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="btn-primary bg-[#F5A547] text-black font-bold px-8 py-4 rounded-full flex items-center justify-center gap-2 neon-orange disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="submit-button"
                >
                  {isSubmitting ? (
                    <>
                      <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Invio in corso...
                    </>
                  ) : (
                    <>
                      <Send className="w-5 h-5" />
                      Invia Richiesta
                    </>
                  )}
                </button>

                <button
                  type="button"
                  onClick={openWhatsApp}
                  className="btn-whatsapp bg-[#25D366] text-white font-bold px-8 py-4 rounded-full flex items-center justify-center gap-2"
                  data-testid="whatsapp-button"
                >
                  <MessageCircle className="w-5 h-5" />
                  Contatta su WhatsApp
                </button>
              </div>
            </form>
          </div>

          {/* Contact Info Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-12">
            <div className="glass-card rounded-xl p-6 text-center" data-testid="contact-info-phone">
              <div className="w-12 h-12 rounded-full bg-[#5CBDB0]/10 flex items-center justify-center mx-auto mb-4">
                <Phone className="w-6 h-6 text-[#5CBDB0]" />
              </div>
              <div className="text-white font-medium mb-1">WhatsApp</div>
              <a href={`https://wa.me/${WHATSAPP_NUMBER.replace('+', '')}`} className="text-[#5CBDB0] hover:underline">
                +39 345 350 9743
              </a>
            </div>

            <div className="glass-card rounded-xl p-6 text-center" data-testid="contact-info-email">
              <div className="w-12 h-12 rounded-full bg-[#F5A547]/10 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-[#F5A547]" />
              </div>
              <div className="text-white font-medium mb-1">Email</div>
              <a href="mailto:info@dodipa.it" className="text-[#F5A547] hover:underline">
                info@dodipa.it
              </a>
            </div>

            <div className="glass-card rounded-xl p-6 text-center" data-testid="contact-info-pec">
              <div className="w-12 h-12 rounded-full bg-[#C14B7E]/10 flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-[#C14B7E]" />
              </div>
              <div className="text-white font-medium mb-1">PEC</div>
              <a href="mailto:fpidea@pec.it" className="text-[#C14B7E] hover:underline">
                fpidea@pec.it
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
